/**
 * Client side of the Settings-managed work-queue vocabularies, plus the work-item
 * shapes both queues share. Mirrors server/services/workQueueLists.service.ts and
 * server/services/epic-blocks/workItem.ts.
 */

import { useEffect, useState } from 'react';

import axios from 'axios';

export type StatusStage = 'open' | 'in-progress' | 'closed';

export interface ListOption {
  value: string;
  label: string;
  retired: boolean;
}

export interface StatusOption extends ListOption {
  stage: StatusStage;
  locked: boolean;
  systemOnly: boolean;
}

export interface ExclusionTypeOption extends ListOption {
  requiresUntil: boolean;
}

export interface NeverBookableDepartment {
  departmentId: string;
  name: string;
  rationale: string;
  evidence: string;
  excludedUntil: string | null;
}

export interface WorkQueueLists {
  statuses: StatusOption[];
  teams: ListOption[];
  exclusionTypes: ExclusionTypeOption[];
  neverBookableDepartments: NeverBookableDepartment[];
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface Person {
  upn: string;
  displayName: string;
}

export interface WorkItemAnnotation {
  status: string;
  stage: StatusStage;
  noteCount: number;
  latestNoteAt: string;
  latestNoteText: string;
  latestAuthorName: string | null;
  addressed: boolean;
  closedAt: string | null;
  closedBy: string | null;
  autoResolved: boolean;
  systemProposedResolution: boolean;
  assignedTeam: string | null;
  assignedTo: Person | null;
  exclusionType: string | null;
  excludedUntil: string | null;
  excludedFromUnresolved: boolean;
  exclusionExpired: boolean;
}

export interface WorkItemNote {
  id: string;
  text: string;
  status: string;
  author: string;
  authorName: string;
  createdAt: string;
  system?: boolean;
  snapshotDate: string | null;
  assignedTeam: string | null;
  assignedTo?: Person | null;
  exclusionType: string | null;
  excludedUntil?: string | null;
  /** Pre-2026-10 name for excludedUntil. */
  reviewAfter?: string | null;
  providerStatus?: string | null;
  providersBehindFix?: number | null;
}

/** What a person submits with a note. */
export interface WorkItemSubmission {
  text: string;
  status: string;
  assignedTeam: string | null;
  assignedTo: Person | null;
  exclusionType: string | null;
  excludedUntil: string | null;
}

/** Used until the server copy arrives; matches the server defaults. */
export const FALLBACK_LISTS: WorkQueueLists = {
  statuses: [
    { value: 'New', label: 'New', retired: false, stage: 'open', locked: true, systemOnly: false },
    { value: 'Investigating', label: 'Investigating', retired: false, stage: 'in-progress', locked: false, systemOnly: false },
    { value: 'Ticket Raised', label: 'Ticket Raised', retired: false, stage: 'in-progress', locked: false, systemOnly: false },
    { value: 'Fixed - Awaiting Snapshot', label: 'Fixed - Awaiting Snapshot', retired: false, stage: 'in-progress', locked: false, systemOnly: false },
    { value: 'Resolved', label: 'Resolved', retired: false, stage: 'closed', locked: true, systemOnly: false },
    { value: 'Auto-Resolved', label: 'Auto-Resolved (rescan)', retired: false, stage: 'closed', locked: true, systemOnly: true }
  ],
  teams: [
    { value: 'Records', label: 'Records', retired: false },
    { value: 'Scheduling', label: 'Scheduling', retired: false },
    { value: 'Enterprise', label: 'Enterprise', retired: false }
  ],
  exclusionTypes: [
    { value: 'Intentional', label: 'Intentional', retired: false, requiresUntil: false },
    { value: 'Temporary', label: 'Temporary', retired: false, requiresUntil: true }
  ],
  neverBookableDepartments: [],
  updatedAt: null,
  updatedBy: null
};

let shared: WorkQueueLists | null = null;
let pending: Promise<WorkQueueLists> | null = null;
const listeners = new Set<(lists: WorkQueueLists) => void>();

function fetchLists(): Promise<WorkQueueLists> {
  if (!pending) {
    pending = axios
      .get<WorkQueueLists>('/api/settings/work-queue-lists', { withCredentials: true })
      .then((response) => {
        shared = response.data;
        return response.data;
      })
      .catch(() => shared ?? FALLBACK_LISTS)
      .finally(() => {
        pending = null;
      });
  }
  return pending;
}

/** Publishes a saved copy to every page using the lists, without a reload. */
export function publishWorkQueueLists(lists: WorkQueueLists): void {
  shared = lists;
  listeners.forEach((listener) => listener(lists));
}

export function useWorkQueueLists(): WorkQueueLists {
  const [lists, setLists] = useState<WorkQueueLists>(shared ?? FALLBACK_LISTS);
  useEffect(() => {
    listeners.add(setLists);
    fetchLists().then(setLists);
    return () => {
      listeners.delete(setLists);
    };
  }, []);
  return lists;
}

export const todayIso = (): string => new Date().toISOString().slice(0, 10);

export const findStatus = (lists: WorkQueueLists, value: string | null | undefined) =>
  lists.statuses.find((status) => status.value === value);

export const statusLabel = (lists: WorkQueueLists, value: string): string =>
  findStatus(lists, value)?.label ?? value;

export const stageOf = (lists: WorkQueueLists, value: string | null | undefined): StatusStage =>
  findStatus(lists, value)?.stage ?? 'open';

export const teamLabel = (lists: WorkQueueLists, value: string): string =>
  lists.teams.find((team) => team.value === value)?.label ?? value;

export const exclusionLabel = (lists: WorkQueueLists, value: string): string =>
  lists.exclusionTypes.find((type) => type.value === value)?.label ?? value;

/** Position in the configured order, so status columns sort by workflow, not alphabet. */
export const statusRank = (lists: WorkQueueLists, value: string | null | undefined): number => {
  const index = lists.statuses.findIndex((status) => status.value === value);
  return index < 0 ? lists.statuses.length : index;
};

/**
 * The status a new note should default to: the current one when a person may
 * still pick it, otherwise the first selectable status at the same stage - so an
 * Auto-Resolved item defaults to Resolved, which is what confirming it means.
 */
export function defaultStatusFor(lists: WorkQueueLists, current: string | null | undefined): string {
  const selectable = lists.statuses.filter((status) => !status.systemOnly && !status.retired);
  if (current && selectable.some((status) => status.value === current)) return current;
  const stage = stageOf(lists, current);
  return (
    selectable.find((status) => status.stage === stage)?.value ??
    selectable[0]?.value ??
    'New'
  );
}

export function isExclusionActive(exclusionType: string | null, excludedUntil: string | null): boolean {
  if (!exclusionType) return false;
  return !excludedUntil || excludedUntil > todayIso();
}

/** The annotation once `note` is the newest note, so a row updates without a refetch. */
export function rollForward(
  previous: WorkItemAnnotation | null | undefined,
  note: WorkItemNote,
  lists: WorkQueueLists,
  proposedResolution: boolean
): WorkItemAnnotation {
  const stage = stageOf(lists, note.status);
  const addressed = stage === 'closed';
  const excludedUntil = note.excludedUntil ?? note.reviewAfter ?? null;
  const excludedFromUnresolved = isExclusionActive(note.exclusionType, excludedUntil);
  const stayedClosed = addressed && previous?.addressed;
  return {
    status: note.status,
    stage,
    noteCount: (previous?.noteCount ?? 0) + 1,
    latestNoteAt: note.createdAt,
    latestNoteText: note.text,
    latestAuthorName: note.authorName,
    addressed,
    closedAt: addressed ? (stayedClosed ? previous!.closedAt : note.createdAt) : null,
    closedBy: addressed ? (stayedClosed ? previous!.closedBy : note.authorName) : null,
    autoResolved: note.status === 'Auto-Resolved',
    systemProposedResolution: !addressed && proposedResolution,
    assignedTeam: note.assignedTeam,
    assignedTo: note.assignedTo ?? null,
    exclusionType: note.exclusionType,
    excludedUntil,
    excludedFromUnresolved,
    exclusionExpired: !!note.exclusionType && !!excludedUntil && !excludedFromUnresolved
  };
}

export const formatWhen = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? new Date(ms).toLocaleString() : iso;
};

export const formatDay = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? new Date(ms).toLocaleDateString() : iso;
};
